import React, { useEffect, useState } from "react";

import {
  CHAPTER_FILTER,
  FANS_NON_MEMBERS_FILTER,
  INACTIVE_FILTER,
  LEADERS_FILTER,
  LOCKED_ARCHIVED_FILTER,
  MEMBERS_ALL_FILTER,
  MEMBERS_NOT_ON_CHANT_FILTER,
  MEMBERS_ON_AUTO_RENEWAL_FILTER,
  MEMBERS_ON_CHANT_FILTER,
  OFFICIAL_CLUB_MEMBER_FILTER,
  OTHER_FILTER,
  PACKAGE_FILTER,
  RECEIVE_EMAILS_NO_FILTER,
  RECEIVE_EMAILS_YES_FILTER,
  SEASON_TICKET_HOLDER_FILTER,
  STATUS_ALL_FILTER,
  VOLUNTEER_NO_FILTER,
  VOLUNTEER_YES_FILTER,
} from "../../../../constants/members";
import { getMinPackageSeasonFromTimeline } from "../../../../helperFunctions/util";
import useAdminDataStore, {
  selectCurrentGroupData,
  selectAllGroups,
} from "../../../../store/adminData/adminData";
import { fetchFormLayout } from "../../../../services/api/packages";
import useGlobalStore from "../../../../store/store";
import { sortObjectByString } from "../../../../utils/helper";
import { EditIcon } from "../../../../utils/icons.jsx";
import { doc, getDoc } from "firebase/firestore";
import { getMyId } from "../../../../services/firebaseService/endPoints/admin/members";
import logger from "../../../../utils/logger";

import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { fetchMembers } from "../../../../services/api/members";
import { db } from "../../../../services/firebaseService/connection";
import useMembersData from "./useMembersData";
import HelpTooltip from "../../../../CommonComponents/HelpTooltip";
import usePackages from "../../../../hooks/usePackages.js";
import TooltipText from "../../../../CommonComponents/TooltipText/TooltipText.jsx";
import MemberName from "./MemberName";

const useMembers = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const allGroups = useAdminDataStore(selectAllGroups);

  const { currentPackages, activePackages, packagesBySeason, hasPackages } =
    usePackages();
  const { formLayout } = useGlobalStore((state) => state.formLayout);
  const { group: paramsGroup, id: paramsId } = useParams();

  const [registrationData, setRegistrationData] = useState(null);
  const [idToAccept, setIdToAccept] = useState("");
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [verifyAddMemberLoading, setVerifyAddMemberLoading] = useState(false);

  const { showAddMember } = useGlobalStore((state) => state.members);

  const [showAddMemberOptions, setShowAddMemberOptions] = useState(false);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);

  const { membersList, membersLoading, familyMembers } = useMembersData();

  const [tableData, setTableData] = useState();
  const [filteredTableData, setFilteredTableData] = useState();

  const [filters, setFilters] = useState({
    searchTerm: "",
    status: STATUS_ALL_FILTER,
    other: OTHER_FILTER,
    package: PACKAGE_FILTER,
    chapter: CHAPTER_FILTER,
    joinedSince: "",
  });

  const handleAddMember = () => {
    if (currentPackages?.length > 0 && showAddMember) {
      handleAddMemberOptionsModal(true);
    } else {
      handleAddMemberFormModal(true);
    }
  };

  const handleAddMemberOptionsModal = (value) => {
    setShowAddMemberOptions(value);
  };

  const handleAddMemberFormModal = (value) => {
    setShowAddMemberForm(value);
  };

  useEffect(() => {
    if (membersList) {
      const tableData = membersList
        .map(({ children, ...item }) => ({ ...item, childMembers: children }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setTableData(tableData);
      setFilteredTableData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersList]);

  const handleUrlRegistration = async () => {
    try {
      setVerifyAddMemberLoading(true);
      const docRef = doc(
        db,
        "transaction_record",
        paramsGroup,
        "records",
        paramsId,
      );
      const docSnapshot = await getDoc(docRef);

      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const myId = getMyId();

        if (
          data?.referrerId === myId &&
          Object.prototype.hasOwnProperty.call(allGroups, paramsGroup)
        ) {
          setRegistrationData(data);
        } else {
          resetAcceptState();
        }
      } else {
        resetAcceptState();
      }
    } catch (err) {
      logger.error(`Error while fetching data: ${err.message}`);
      resetAcceptState();
    } finally {
      setVerifyAddMemberLoading(false);
    }
  };

  const resetAcceptState = () => {
    setIdToAccept("");
    setShowAddMemberModal(false);
  };

  const resetMemberFilters = () => {
    setFilters({
      searchTerm: "",
      status: STATUS_ALL_FILTER,
      other: OTHER_FILTER,
      package: PACKAGE_FILTER,
      chapter: CHAPTER_FILTER,
      joinedSince: "",
    });
  };

  const addMember = async () => {
    const url = `${import.meta.env.VITE_GCP_BASE_URL}/stripe2/writeDataToFirebase`;
    const metadata = registrationData?.data;
    const groupId = groupData?.id;

    const reqBody = {
      metadata: metadata,
      groupId: groupId,
    };

    try {
      setAddMemberLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reqBody),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      fetchMembers(groupData.id);
      setIdToAccept("");
      setShowAddMemberModal(false);
      setRegistrationData({});
    } catch (err) {
      logger.error(err);
    } finally {
      setAddMemberLoading(false);
    }
  };

  useEffect(() => {
    // if (!membersList) {
    //   fetchMembers(groupData?.id);
    // }

    resetMemberFilters();

    if (!formLayout?.[groupData?.id]) {
      fetchFormLayout(groupData?.id);
    }

    resetMemberFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const handleFilterChange = (type, selectedValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [type]: selectedValue,
    }));
  };

  const handleDropdownChange = (type, selectedValue) => {
    handleFilterChange(type, selectedValue);
  };

  const handleSearchInputChange = (value) => {
    handleFilterChange("searchTerm", value);
  };

  const applyFilters = (data) => {
    return data.filter((item) => {
      // Hide members who left the group (Manage Groups > Delete) unless they have an active member package
      if (item?.hideGroup === true) {
        const hasActivePackage =
          item?.subscriptionDetail?.status === "active" ||
          (hasPackages &&
            item?.package &&
            item?.packageSeason &&
            item?.userRole !== "fan" &&
            activePackages.find(
              (pkg) => pkg.key === `${item.package}|${item.packageSeason}`,
            ));
        if (!hasActivePackage) {
          return false;
        }
      }

      if (filters?.searchTerm) {
        const searchTermLower = filters.searchTerm.toLowerCase();
        const nameLower = item?.name?.toLowerCase();
        const emailLower = item?.email?.toLowerCase();

        if (
          !(
            nameLower?.includes(searchTermLower) ||
            emailLower?.includes(searchTermLower)
          )
        ) {
          return false;
        }
      }

      if (filters.joinedSince) {
        const registeredTimestamp = item?.registrationDate || 0;
        const joinedSinceTimestamp = filters.joinedSince;

        const registeredDate =
          String(registeredTimestamp).length === 10
            ? dayjs.unix(registeredTimestamp)
            : dayjs(registeredTimestamp);

        const joinedSinceDate =
          String(joinedSinceTimestamp).length === 10
            ? dayjs.unix(joinedSinceTimestamp)
            : dayjs(joinedSinceTimestamp);

        if (registeredDate.isBefore(joinedSinceDate)) {
          return false;
        }
      }

      if (
        filters.package &&
        filters.package.length > 0 &&
        filters.package !== PACKAGE_FILTER
      ) {
        let packageSeason = "";

        if (item?.packageSeason) {
          packageSeason = item?.packageSeason;
        } else {
          const pkgId = filters?.package?.split("|")?.[0];

          const pkgDetails = currentPackages?.find((pkg) => pkg?.id === pkgId);

          if (pkgDetails) {
            packageSeason = getMinPackageSeasonFromTimeline(
              pkgDetails?.packageTimeline,
            );
          }
        }

        const pkgKey = item?.package
          ? packageSeason
            ? `${item?.package}|${packageSeason}`
            : item?.package
          : "";
        if (pkgKey !== filters?.package) {
          return false;
        }
      }

      if (
        filters.chapter &&
        filters.chapter !== CHAPTER_FILTER &&
        item?.chapter !== filters.chapter
      ) {
        return false;
      }

      if (
        filters.other === OFFICIAL_CLUB_MEMBER_FILTER &&
        !item.officialClubMember
      ) {
        return false;
      }

      if (
        filters.other === SEASON_TICKET_HOLDER_FILTER &&
        (!item.seasonTicketHolder ||
          Object.keys(item.seasonTicketHolder || {}).length === 0 ||
          Object.keys(item.seasonTicketHolder || {})
            .map((key) => item.seasonTicketHolder[key])
            .includes(""))
      ) {
        return false;
      }
      if (
        filters.other === VOLUNTEER_YES_FILTER &&
        !item.shareVolunteerOpportunities
      ) {
        return false;
      }

      if (
        filters.other === VOLUNTEER_NO_FILTER &&
        item.shareVolunteerOpportunities
      ) {
        return false;
      }
      if (
        filters.other === RECEIVE_EMAILS_YES_FILTER &&
        !item.joinedEmailList
      ) {
        return false;
      }
      if (filters.other === RECEIVE_EMAILS_NO_FILTER && item.joinedEmailList) {
        return false;
      }
      // if (
      //   filters.other === DONATED_FILTER &&
      //   (!item.donationAmount ||
      //     !item.donationAmount.value ||
      //     item.donationAmount.value === "0")
      // ) {
      //   return false;
      // }

      if (filters.status === STATUS_ALL_FILTER && item.lockStatus) {
        return false;
      }

      if (filters.status === MEMBERS_ALL_FILTER) {
        if (hasPackages) {
          if (item.lockStatus) {
            return false;
          } else {
            if (groupData.requireMembershipPackage) {
              if (
                !(
                  item.package &&
                  item.package?.length > 0 &&
                  item.packageSeason &&
                  activePackages.find(
                    (pkg) =>
                      pkg.key === `${item.package}|${item.packageSeason}` &&
                      item.userRole !== "fan",
                  )
                )
              ) {
                return false;
              }
            } else {
              if (item.userRole === "fan") {
                return false;
              }
            }
          }
        }
      }

      if (filters.status === MEMBERS_ON_CHANT_FILTER) {
        if (item.lockStatus || item.joinedChant === "No") {
          return false;
        }
      }

      if (filters.status === MEMBERS_NOT_ON_CHANT_FILTER) {
        if (item.lockStatus || item.joinedChant === "Yes") {
          return false;
        }
      }

      if (filters.status === MEMBERS_ON_AUTO_RENEWAL_FILTER) {
        if (item?.subscriptionDetail?.status !== "active") {
          return false;
        }
      }

      if (
        filters.status === LEADERS_FILTER &&
        (item.lockStatus ||
          item.userRole === "member" ||
          item.userRole === "fan" ||
          !item.userRole)
      ) {
        return false;
      }

      if (
        filters.status === FANS_NON_MEMBERS_FILTER &&
        (item.lockStatus || item.userRole !== "fan")
      ) {
        return false;
      }
      if (filters.status === INACTIVE_FILTER) {
        if (item.lockStatus) {
          return false;
        } else {
          if (groupData.requireMembershipPackage) {
            if (item.userRole === "admin" || item.userRole === "fan") {
              return false;
            }

            if (
              item.package &&
              item.package?.length > 0 &&
              item.packageSeason &&
              activePackages.find(
                (pkg) =>
                  pkg.key === `${item.package}|${item.packageSeason}` &&
                  item.userRole !== "fan",
              )
            ) {
              return false;
            }
          } else {
            return false;
          }
        }
      }
      if (filters.status === LOCKED_ARCHIVED_FILTER && !item.lockStatus) {
        return false;
      }

      return true;
    });
  };

  useEffect(() => {
    if (tableData?.length > 0) {
      const filteredData = applyFilters(tableData);
      setFilteredTableData(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, tableData]);

  const [editableRecordId, setEditableRecordId] = useState("");
  const [editableRecord, setEditableRecord] = useState({});
  const [linkedMember, setLinkedMember] = useState({});

  const handleModalOpen = async (id, familyMembershipId) => {
    if (id) {
      const selectedRecord = filteredTableData.find((item) => item.id === id);
      setEditableRecord(selectedRecord);
    } else {
      setEditableRecordId("");
      setEditableRecord({});
    }

    if (familyMembershipId) {
      const linkedMember = membersList?.find(
        (member) =>
          member?.familyMembershipId === familyMembershipId && member.id !== id,
      );
      setLinkedMember(linkedMember);
    } else {
      setLinkedMember(null);
    }

    setEditableRecordId(id);
  };

  useEffect(() => {
    if (paramsId && paramsId?.length > 0) {
      setIdToAccept(paramsId);
      setShowAddMemberModal(true);
      handleUrlRegistration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let columns = [
    {
      title: "Actions",
      width: 100,
      align: "center",
      render: (data) => {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {data && data.id && (
              <i
                className="ml-3"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  handleModalOpen(data.id, data.familyMembershipId)
                }
              >
                <img
                  src={EditIcon}
                  alt="edit-member"
                  style={{ width: 25, height: 25 }}
                />
              </i>
            )}
          </div>
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => sortObjectByString(a, b, "name"),
      render: (name, record) => (
        <MemberName
          name={name}
          record={record}
          groupData={groupData}
          activePackages={activePackages}
          hasPackages={hasPackages}
          familyMembers={familyMembers}
        />
      ),
      width: window.screen.width > 500 ? 300 : 150,
    },

    {
      title: "Email",
      width: window.screen.width > 500 ? 300 : 150,
      dataIndex: "email",
      sorter: (a, b) => sortObjectByString(a, b, "email"),
    },
    {
      title: (
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <span className="flex-center">Phone</span>
          <HelpTooltip
            text="Phone number displayed is what the member shared when buying
                  their membership package. It does not control app
                  authentication. We hide that number, treating it like a
                  Username."
          />
        </div>
      ),
      width: window.screen.width > 500 ? 200 : 150,
      dataIndex: "phone",
    },
    {
      title: "Birthday",
      width: 150,
      dataIndex: "birthday",
      render: (data) => {
        if (data && typeof data.month === "string") {
          const dayValue = String(data.day ?? data.date ?? "").trim();
          const isValidDay = /^\d+$/.test(dayValue);

          if (isValidDay) {
            let birthday = `${data.month} ${dayValue}`;
            if (data.year && /^\d{4}$/.test(String(data.year).trim())) {
              birthday = `${birthday} ${data.year}`;
            }
            return <span>{birthday}</span>;
          }
        }
        return "";
      },
    },
    {
      title: "Hometown",
      dataIndex: "hometown",
      key: "hometown",
      width: 150,

      sorter: (a, b) => sortObjectByString(a, b, "hometown"),
    },
    {
      title: "Street",
      key: "streetName",
      width: window.screen.width > 500 ? 200 : 150,
      dataIndex: "address1",

      render: (data) => <span style={{}}>{data}</span>,
    },
    {
      title: "Address 2",
      key: "address2",

      dataIndex: "address2",
      render: (data) => <span style={{}}>{data}</span>,
      width: window.screen.width > 500 ? 200 : 150,
    },
    {
      title: "City",
      key: "city",

      dataIndex: "city",
      render: (data) => <span style={{}}>{data}</span>,
      width: window.screen.width > 500 ? 200 : 150,
    },
    {
      title: "State",
      key: "state",

      dataIndex: "state",
      render: (data) => <span style={{}}>{data}</span>,
      width: window.screen.width > 500 ? 200 : 150,
    },
    {
      title: "Country",
      key: "country",

      dataIndex: "country",
      render: (data) => <span style={{}}>{data}</span>,
      width: window.screen.width > 500 ? 200 : 150,
    },
    {
      title: "Zip",
      key: "zip",

      dataIndex: "zip",
      render: (data) => <span style={{}}>{data}</span>,
      width: window.screen.width > 500 ? 200 : 150,
    },

    {
      title: "Member Since",
      dataIndex: "memberSince",
      sorter: (a, b) => sortObjectByString(a, b, "memberSince"),
      width: 150,
    },
    {
      title: "Membership #",
      dataIndex: "membershipNumber",
      sorter: (a, b) => {
        const aVal = a?.membershipNumber ? String(a.membershipNumber) : "";
        const bVal = b?.membershipNumber ? String(b.membershipNumber) : "";
        return aVal.localeCompare(bVal, undefined, { numeric: true });
      },
      width: 150,
    },
    {
      title: <TooltipText title="Official Club Member #" maxWidth={120} />,
      key: "officialClubMember",
      dataIndex: "officialClubMember",
      width: 150,
    },
    {
      title: <TooltipText title="Receive Email?" maxWidth={70} />,
      key: "joinedEmailList",
      dataIndex: "joinedEmailList",
      width: 100,
    },
    {
      title: "Volunteer?",
      key: "notifyForVolunteerOpportunities",
      dataIndex: "shareVolunteerOpportunities",
      width: 120,
    },
    {
      title: <TooltipText title="Season Ticket" maxWidth={70} />,
      key: "seasonTicketHolder",
      dataIndex: "seasonTicketHolder",
      render: (data) =>
        data &&
        Object.keys(data).length > 0 &&
        `${data.section || data.season || ""}/${data.seats || data.noOfSeats || ""}`,
      width: 100,
    },
    {
      title: <TooltipText title="Registration (Group)" maxWidth={120} />,
      key: "registeredOn",
      showSorterTooltip: false,
      dataIndex: "registeredOn",
      width: 150,
      render: (timestamp, record) => {
        if (timestamp || record?.subscriptionDetail?.lastDate) {
          let exactDate = record?.subscriptionDetail?.lastDate || timestamp;

          const isUnix = exactDate.toString().length === 10;
          let date = isUnix ? dayjs.unix(exactDate) : dayjs(exactDate);

          if (date.isValid()) {
            date = date.format("MMM Do, YYYY");
          } else {
            date = "";
          }
          return <span>{date}</span>;
        }
      },
      sorter: (a, b) => {
        if (!a?.registeredOn && !b?.registeredOn) return 0;
        if (!a?.registeredOn) return -1;
        if (!b?.registeredOn) return 1;

        const aDate = Number(a.registeredOn);
        const bDate = Number(b.registeredOn);

        if (isNaN(aDate) || isNaN(bDate)) return 0;

        const aSeconds = aDate.toString().length > 10 ? aDate / 1000 : aDate;
        const bSeconds = bDate.toString().length > 10 ? bDate / 1000 : bDate;

        return aSeconds - bSeconds;
      },
    },
    {
      title: <TooltipText title="Registration (Chant)" maxWidth={120} />,
      showSorterTooltip: false,
      key: "registeredOnChantDate",
      dataIndex: "registeredOnChantDate",
      render: (timestamp, record) => {
        if (record?.hideGroup) return null;
        if (timestamp) {
          const isUnix = timestamp.toString().length === 10;
          let date = isUnix ? dayjs.unix(timestamp) : dayjs(timestamp);

          if (date.isValid()) {
            date = date.format("MMM Do, YYYY");
          } else {
            date = "";
          }
          return <span>{date}</span>;
        }
      },
      width: 150,
      sorter: (a, b) => {
        if (!a?.registeredOnChantDate && !b?.registeredOnChantDate) return 0;
        if (!a?.registeredOnChantDate) return -1;
        if (!b?.registeredOnChantDate) return 1;

        const aDate = Number(a.registeredOnChantDate);
        const bDate = Number(b.registeredOnChantDate);

        if (isNaN(aDate) || isNaN(bDate)) return 0;

        const aSeconds = aDate.toString().length > 10 ? aDate / 1000 : aDate;
        const bSeconds = bDate.toString().length > 10 ? bDate / 1000 : bDate;

        return aSeconds - bSeconds;
      },
    },
    {
      title: "Chapter",
      width: 200,

      dataIndex: "chapter",
    },
    {
      title: "Referred By",
      width: 200,
      dataIndex: "referredBy",
    },
  ];

  if (groupData?.clubId === "529") {
    const membershipColIndex = columns.findIndex(
      (col) => col.dataIndex === "membershipNumber",
    );
    columns.splice(membershipColIndex + 1, 0, {
      title: "Group Member #",
      dataIndex: "groupMemberNumber",
      sorter: (a, b) => {
        const aVal = a?.groupMemberNumber ?? Infinity;
        const bVal = b?.groupMemberNumber ?? Infinity;
        return aVal - bVal;
      },
      width: 175,
    });
  }

  if (currentPackages && currentPackages?.length > 0) {
    columns.splice(2, 0, {
      title: "Package",
      width: 300,
      key: "package",
      dataIndex: ["packageSeason", "package"],
      render: (_, record) => {
        const getPackageName = () => {
          let packageName = "";
          let packageObj = null;

          let packageSeason = null;

          if (record?.packageSeason) {
            packageSeason = record?.packageSeason || null;
          } else {
            const pkgDetails = currentPackages?.find(
              (pkg) => pkg?.id === record?.package,
            );

            if (pkgDetails) {
              packageSeason = getMinPackageSeasonFromTimeline(
                pkgDetails?.packageTimeline,
              );
            }
          }

          packageObj = record?.package
            ? packagesBySeason.find(
                (pack) => pack?.key === `${record?.package}|${packageSeason}`,
              )
            : null;

          if (packageObj) {
            packageName = packageObj?.value || "";
          }

          return packageName;
        };

        return (
          <div className="d-flex">
            <span>{getPackageName()}</span>
          </div>
        );
      },
    });
  }

  Object.keys(formLayout?.[groupData?.id]?.customFieldDetails || {}).forEach(
    (key) => {
      let fieldTitle =
        formLayout?.[groupData?.id]?.customFieldDetails[key].fieldTitle;

      let fieldType =
        formLayout?.[groupData?.id]?.customFieldDetails[key].fieldType;

      let dataIndex = key;
      let col = {
        title: <TooltipText title={fieldTitle} maxWidth={120} />,
        key: key,
        dataIndex,
        width: 200,
        ellipsis: true,
        render: (data) => {
          if (fieldType === "date") {
            return data ? <span>{dayjs(data).format("MMM D, YYYY")}</span> : "";
          }
          return <span>{data}</span>;
        },
      };

      columns.push(col);
    },
  );

  return {
    handleAddMember,
    showAddMemberForm,
    handleAddMemberFormModal,
    showAddMemberOptions,
    handleAddMemberOptionsModal,
    membersList,
    filteredTableData,
    handleSearchInputChange,
    handleFilterChange,
    currentPackages,
    formLayout,
    groupData,
    handleDropdownChange,
    filters,
    membersLoading,
    editableRecord,
    editableRecordId,
    handleModalOpen,
    linkedMember,
    idToAccept,
    showAddMemberModal,
    addMember,
    setShowAddMemberModal,
    registrationData,
    columns,
    verifyAddMemberLoading,
    addMemberLoading,
  };
};

export default useMembers;
